import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSupportMessages,
  markAsReadByAdmin,
} from "../../../features/supportChat/supportChatSlice";
import ScrollToBottomIcon from "../../icons/ScrollToBottomIcon";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import Loader from "../../loader/Loader";
import TextWrapper from "../../textWrapper/TextWrapper";
import ChatMessage from "../chatMessage/ChatMessage";
import styles from "./chatContainer.module.css";

const ChatContainer = ({ justSentMessage }) => {
  const chatRef = useRef();
  const scrollTopBeforeFetch = useRef(0);
  const isFetchingOlder = useRef(false);
  const hasMarkedRead = useRef(false);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const dispatch = useDispatch();

  const scrollToBottom = () => {
    const container = chatRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const {
    activeChatId,
    messages: allMessages,
    chats,
  } = useSelector((state) => state.supportChat);

  const chatMessages = allMessages[activeChatId]?.items || [];
  const isLoading = allMessages[activeChatId]?.isLoading;
  const hasMore = allMessages[activeChatId]?.hasMore;

  const unseenByAdmin = chats.find(
    (chat) => chat._id === activeChatId
  )?.unreadByAdminCount;

  useEffect(() => {
    isFetchingOlder.current = false;
    scrollTopBeforeFetch.current = 0;
  }, [activeChatId]);

  useEffect(() => {
    if (!activeChatId) return;

    dispatch(fetchSupportMessages({ chatId: activeChatId }));
  }, [activeChatId]);

  useEffect(() => {
    if (chatMessages.length > 0 && !isLoading) {
      scrollToBottom();
    }
  }, [chatMessages.length, isLoading, activeChatId]);

  useLayoutEffect(() => {
    const container = chatRef.current;
    if (!container || !isFetchingOlder.current) return;

    requestAnimationFrame(() => {
      const heightDiff = container.scrollHeight - scrollTopBeforeFetch.current;
      container.scrollTop = heightDiff;
      isFetchingOlder.current = false;
    });
  }, [chatMessages]);

  const handleLoadMore = () => {
    const firstMessage = chatMessages[0];
    if (!firstMessage?._id || !chatRef.current) return;

    scrollTopBeforeFetch.current = chatRef.current.scrollHeight;
    isFetchingOlder.current = true;

    dispatch(
      fetchSupportMessages({
        chatId: activeChatId,
        beforeId: firstMessage._id,
      })
    );
  };

  useEffect(() => {
    if (justSentMessage?.current) {
      requestAnimationFrame(() => {
        scrollToBottom();
        justSentMessage.current = false;
      });
    }
  }, [chatMessages.length]);

  useEffect(() => {
    const container = chatRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isAtBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        100;

      setShowScrollToBottom(!isAtBottom);

      if (isAtBottom && unseenByAdmin > 0 && activeChatId) {
        dispatch(markAsReadByAdmin(activeChatId));
        hasMarkedRead.current = true;
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [unseenByAdmin, activeChatId, dispatch]);

  useEffect(() => {
    hasMarkedRead.current = false;
  }, [activeChatId, unseenByAdmin]);

  useInfiniteScroll({
    ref: chatRef,
    hasMore,
    isLoading,
    onLoadMore: handleLoadMore,
    direction: "up",
    threshold: 80,
  });

  return (
    <div className={styles.container}>
      <div className={styles.scrollBody} ref={chatRef}>
        {isLoading && hasMore && (
          <div className={styles.loaderContainer}>
            <Loader />
          </div>
        )}

        {chatMessages.map((item) => (
          <ChatMessage key={item._id} chat={item} />
        ))}
      </div>

      {(showScrollToBottom || unseenByAdmin > 0) && (
        <div className={styles.scrollToTopWrapper} onClick={scrollToBottom}>
          <ScrollToBottomIcon className={styles.arrowDown} />
          {unseenByAdmin > 0 && (
            <div className={styles.unseenIndicater}>
              <TextWrapper textStyle={"textXSmall"}>
                {unseenByAdmin}
              </TextWrapper>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatContainer;
